import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import { useFormik } from 'formik';
import { type ApolloQueryResult } from '@apollo/react-hooks';

import { object, number, string } from 'yup';
import type { RatingForm, DimensionScores } from './types';
import { useUpdateApplicationReview } from '../../../../graphql';
import { PANEL_APPLICATION_REVIEWER_TEXT } from '../../../../lang';
import { FundingSource } from '../../../../../Nomination/components';
import {
  Button,
  Dialog,
  TextField,
  Alert,
  type AlertDetails,
} from '../../../../../../components';
import { theme } from '../../../../../../Theme';
import { handleNumberKeyDown } from '../../../../../../utils';

type ApplicationReviewValue = {
  applicantName: string;
  applicationNo: string;
  fundingSource: FundingSource;
  panelId: string;
  applicationId: string;
  amendedScoresData: RatingForm;
  scores: DimensionScores;
  refetch: () => Promise<ApolloQueryResult<any>>;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

type ApplicationReviewDialogProps = {
  openDialog: boolean;
  dialogContent: ApplicationReviewValue;
  onDialogClose: () => void;
};

const checkInputScore = (value: any): boolean => {
  return value !== undefined && value !== null && value !== '';
};

export const ApplicationReviewDialog: React.FC<ApplicationReviewDialogProps> = (
  props: ApplicationReviewDialogProps,
) => {
  const { openDialog, dialogContent, onDialogClose } = props;
  const {
    applicantName,
    applicationNo,
    amendedScoresData,
    scores,
    applicationId,
    panelId,
    fundingSource,
    refetch,
    handleLoading,
  } = dialogContent;
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const isHKPFS =
    fundingSource === FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME;
  const ratingMapping = [
    {
      key: 'AE',
      label: PANEL_APPLICATION_REVIEWER_TEXT.AE,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.AE,
      score: scores.AE,
      comment: 'AEComment',
    },
    {
      key: 'RP',
      label: PANEL_APPLICATION_REVIEWER_TEXT.RP,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.RP_TOOLTIP,
      score: scores.RP,
      comment: 'RPComment',
    },
    {
      key: 'LS',
      label: PANEL_APPLICATION_REVIEWER_TEXT.LS,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.LS_TOOLTIP,
      score: scores.LS,
      comment: 'LSComment',
    },
    {
      key: 'CI',
      label: PANEL_APPLICATION_REVIEWER_TEXT.CI,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.CI_TOOLTIP,
      score: scores.CI,
      comment: 'CIComment',
    },
    ...(isHKPFS
      ? []
      : [
          {
            key: 'RE',
            label: PANEL_APPLICATION_REVIEWER_TEXT.RE,
            tooltip: PANEL_APPLICATION_REVIEWER_TEXT.RE,
            score: scores.RE,
            comment: 'REComment',
          },
          {
            key: 'OS',
            label: PANEL_APPLICATION_REVIEWER_TEXT.OS,
            tooltip: PANEL_APPLICATION_REVIEWER_TEXT.OS,
            score: scores.OS,
            comment: 'OSComment',
          },
        ]),
    {
      key: 'CD',
      label: PANEL_APPLICATION_REVIEWER_TEXT.CD,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.CD,
      score: scores.CD,
      comment: 'CDComment',
    },
    {
      key: 'US',
      label: PANEL_APPLICATION_REVIEWER_TEXT.US,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.US,
      score: scores.US,
      comment: 'USComment',
    },
  ];

  const updateApplicationReview = useUpdateApplicationReview();
  const handleUpdateApplicationReview = async (
    values: RatingForm,
  ): Promise<void> => {
    const { AE, RP, LS, CI, RE, OS, CD, US, totalScore, ...rest } = values;
    await handleLoading(async () => {
      try {
        await updateApplicationReview({
          variables: {
            input: {
              applicationId,
              panelId,
              scoresOverride: {
                // current the amended score is not a must, if no input, will give null
                funding: fundingSource,
                totalScore: undefined, // do not pass this field to backend
                AE: checkInputScore(AE) ? Number(AE) : null,
                RP: checkInputScore(RP) ? Number(RP) : null,
                LS: checkInputScore(LS) ? Number(LS) : null,
                CI: checkInputScore(CI) ? Number(CI) : null,
                RE: isHKPFS ? null : checkInputScore(RE) ? Number(RE) : null,
                OS: isHKPFS ? null : checkInputScore(OS) ? Number(OS) : null,
                CD: checkInputScore(CD) ? Number(CD) : null,
                US: checkInputScore(US) ? Number(US) : null,
                ...rest,
              },
            },
          },
        });
        await refetch();

        setAlertDetails({
          alertContent: PANEL_APPLICATION_REVIEWER_TEXT.AMEND_RATING_SUCCESS,
          alertType: 'success',
        });
      } catch (error) {
        setAlertDetails({
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        });
      }
    });
    setOpenAlert(true);
  };

  const handleViewMonitor = (): void => {
    window.open(`/internal/monitor/${applicationId}`);
  };

  const scoresSchema = number().min(0, 'out of range').max(5, 'out of range');
  const formik = useFormik<RatingForm>({
    initialValues: amendedScoresData,
    validationSchema: object({
      AE: scoresSchema,
      RP: scoresSchema,
      LS: scoresSchema,
      CI: scoresSchema,
      RE: scoresSchema,
      OS: scoresSchema,
      CD: scoresSchema,
      US: scoresSchema,
      AEComment: string().when('AE', ([AE], schema) => {
        const present = checkInputScore(AE);
        return present ? schema.required('Required') : schema.notRequired();
      }),
      RPComment: string().when('RP', ([RP], schema) => {
        const present = checkInputScore(RP);
        return present ? schema.required('Required') : schema.notRequired();
      }),
      LSComment: string().when('LS', ([LS], schema) => {
        const present = checkInputScore(LS);
        return present ? schema.required('Required') : schema.notRequired();
      }),
      CIComment: string().when('CI', ([CI], schema) => {
        const present = checkInputScore(CI);
        return present ? schema.required('Required') : schema.notRequired();
      }),
      REComment: string().when('RE', ([RE], schema) => {
        const present = checkInputScore(RE);
        return present ? schema.required('Required') : schema.notRequired();
      }),
      OSComment: string().when('OS', ([OS], schema) => {
        const present = checkInputScore(OS);
        return present ? schema.required('Required') : schema.notRequired();
      }),
      CDComment: string().when('CD', ([CD], schema) => {
        const present = checkInputScore(CD);
        return present ? schema.required('Required') : schema.notRequired();
      }),
      USComment: string().when('US', ([US], schema) => {
        const present = checkInputScore(US);
        return present ? schema.required('Required') : schema.notRequired();
      }),
    }),
    onSubmit: handleUpdateApplicationReview,
  });

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Dialog
        dialogTitle={PANEL_APPLICATION_REVIEWER_TEXT.APPLICATION_REVIEW}
        openDialog={openDialog}
        onDialogClose={onDialogClose}
        scroll="body"
        width="40rem"
      >
        <form
          className="flex flex-col px-6 pb-4"
          noValidate
          onSubmit={formik.handleSubmit}
        >
          <div
            className={`text-[${theme.palette.primary.main}] mb-2 underline cursor-pointer`}
            onClick={handleViewMonitor}
          >{`${PANEL_APPLICATION_REVIEWER_TEXT.PROFILE} ${applicantName} (${applicationNo})`}</div>
          <div>{PANEL_APPLICATION_REVIEWER_TEXT.REVIEW_GUIDANCE}</div>
          <div
            className="mt-2 flex flex-row w-full grid grid-cols-4 font-medium word-break text-center gap-1"
            style={{ gridTemplateColumns: '4fr 3fr 3fr 4fr' }}
          >
            <span />
            <span>{PANEL_APPLICATION_REVIEWER_TEXT.DEPT_RATING}</span>
            <span>{PANEL_APPLICATION_REVIEWER_TEXT.AMENDED_RATING}</span>
            <span>{PANEL_APPLICATION_REVIEWER_TEXT.COMMENT}</span>
          </div>
          <div className="my-2">
            {ratingMapping.map((rating, index) => {
              return (
                <div
                  key={index}
                  className="flex flex-row w-full grid grid-cols-4 word-break text-center gap-4 mb-3 content-start"
                  style={{ gridTemplateColumns: '4fr 3fr 3fr 4fr' }}
                >
                  <Tooltip title={rating.tooltip ?? ''} arrow>
                    <span
                      className={`text-start flex items-center font-medium h-8 text-[${theme.palette.primary.main}]`}
                    >
                      {rating.label}
                    </span>
                  </Tooltip>
                  <span className="font-medium h-8 items-center flex justify-center">
                    {rating.score}
                  </span>
                  <TextField
                    fullWidth
                    sx={{
                      padding: '0 1rem',
                    }}
                    type="number"
                    size="small"
                    name={rating.key}
                    value={formik.values[rating.key as keyof RatingForm]}
                    onKeyDown={handleNumberKeyDown}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched[rating.key as keyof RatingForm] &&
                      Boolean(formik.errors[rating.key as keyof RatingForm])
                    }
                    helperText={
                      formik.touched[rating.key as keyof RatingForm] &&
                      formik.errors[rating.key as keyof RatingForm]
                    }
                  />
                  <TextField
                    fullWidth
                    type="text"
                    size="small"
                    name={rating.comment}
                    value={formik.values[rating.comment as keyof RatingForm]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched[rating.comment as keyof RatingForm] &&
                      Boolean(formik.errors[rating.comment as keyof RatingForm])
                    }
                    helperText={
                      formik.touched[rating.comment as keyof RatingForm] &&
                      formik.errors[rating.comment as keyof RatingForm]
                    }
                  />
                </div>
              );
            })}
          </div>
          <div className="w-fill flex flex-row gap-4 items-center justify-center my-2">
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: 'none',
              }}
            >
              {PANEL_APPLICATION_REVIEWER_TEXT.CONFIRM}
            </Button>
            <Button
              variant="outlined"
              sx={{
                textTransform: 'none',
              }}
              onClick={() => {
                onDialogClose();
                formik.resetForm();
              }}
            >
              {PANEL_APPLICATION_REVIEWER_TEXT.CANCEL}
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
};
